import { useRef, useEffect, useState, useCallback, MouseEvent, KeyboardEvent, WheelEvent } from 'react';
import {
  Monitor,
  MonitorOff,
  Maximize2,
  Minimize2,
  PhoneOff,
  Loader2,
  AlertCircle,
  Signal,
  SignalHigh,
  SignalMedium,
  SignalLow,
  Mouse,
  MousePointer,
  Keyboard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { RemoteSession, RemoteInputEvent, ConnectionState } from '@/hooks/useRemoteSession';

interface RemoteSessionViewerProps {
  session: RemoteSession | null;
  stream: MediaStream | null;
  connectionState: ConnectionState;
  error: string | null;
  onEndSession: () => void;
  onSendInput: (event: RemoteInputEvent) => void;
  isEndingSession?: boolean;
  className?: string;
}

// Connection quality indicator
function ConnectionQuality({ quality }: { quality: string | null }) {
  if (!quality) return null;

  const icons = {
    excellent: <SignalHigh className="h-4 w-4 text-emerald-500" />,
    good: <SignalHigh className="h-4 w-4 text-green-500" />,
    fair: <SignalMedium className="h-4 w-4 text-amber-500" />,
    poor: <SignalLow className="h-4 w-4 text-red-500" />,
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1.5">
          {icons[quality as keyof typeof icons] || <Signal className="h-4 w-4" />}
          <span className="text-xs capitalize">{quality}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>Connection quality: {quality}</TooltipContent>
    </Tooltip>
  );
}

// Status badge component
function StatusBadge({ state }: { state: ConnectionState }) {
  const variants: Record<ConnectionState, { variant: 'secondary' | 'default' | 'destructive'; label: string; className?: string }> = {
    disconnected: { variant: 'secondary', label: 'Disconnected' },
    connecting: { variant: 'default', label: 'Connecting...' },
    connected: { variant: 'default', label: 'Connected', className: 'bg-emerald-500' },
    failed: { variant: 'destructive', label: 'Failed' },
  };

  const { variant, label, className } = variants[state];

  return (
    <Badge variant={variant} className={cn('gap-1.5', className)}>
      {state === 'connecting' && <Loader2 className="h-3 w-3 animate-spin" />}
      {state === 'connected' && <div className="h-2 w-2 rounded-full bg-white animate-pulse" />}
      {label}
    </Badge>
  );
}

export function RemoteSessionViewer({
  session,
  stream,
  connectionState,
  error,
  onEndSession,
  onSendInput,
  isEndingSession = false,
  className,
}: RemoteSessionViewerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsEnabled, setControlsEnabled] = useState(true);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Track video dimensions for coordinate mapping
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const updateDimensions = () => {
      setVideoDimensions({
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };

    video.addEventListener('loadedmetadata', updateDimensions);
    video.addEventListener('resize', updateDimensions);

    return () => {
      video.removeEventListener('loadedmetadata', updateDimensions);
      video.removeEventListener('resize', updateDimensions);
    };
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Calculate coordinates relative to remote screen
  const calculateRemoteCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      if (!videoRef.current || videoDimensions.width === 0) {
        return { x: 0, y: 0 };
      }

      const rect = videoRef.current.getBoundingClientRect();
      const scaleX = videoDimensions.width / rect.width;
      const scaleY = videoDimensions.height / rect.height;

      return {
        x: Math.round((clientX - rect.left) * scaleX),
        y: Math.round((clientY - rect.top) * scaleY),
      };
    },
    [videoDimensions]
  );

  // Mouse event handlers
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      const { x, y } = calculateRemoteCoordinates(e.clientX, e.clientY);
      onSendInput({ type: 'mouse', mouseType: 'move', x, y });
    },
    [controlsEnabled, session?.session_type, calculateRemoteCoordinates, onSendInput]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      e.preventDefault();
      const { x, y } = calculateRemoteCoordinates(e.clientX, e.clientY);
      onSendInput({ type: 'mouse', mouseType: 'down', x, y, button: e.button });
    },
    [controlsEnabled, session?.session_type, calculateRemoteCoordinates, onSendInput]
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      e.preventDefault();
      const { x, y } = calculateRemoteCoordinates(e.clientX, e.clientY);
      onSendInput({ type: 'mouse', mouseType: 'up', x, y, button: e.button });
    },
    [controlsEnabled, session?.session_type, calculateRemoteCoordinates, onSendInput]
  );

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      const { x, y } = calculateRemoteCoordinates(e.clientX, e.clientY);
      onSendInput({ type: 'mouse', mouseType: 'click', x, y, button: e.button });
    },
    [controlsEnabled, session?.session_type, calculateRemoteCoordinates, onSendInput]
  );

  const handleDoubleClick = useCallback(
    (e: MouseEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      const { x, y } = calculateRemoteCoordinates(e.clientX, e.clientY);
      onSendInput({ type: 'mouse', mouseType: 'dblclick', x, y, button: e.button });
    },
    [controlsEnabled, session?.session_type, calculateRemoteCoordinates, onSendInput]
  );

  // Scroll handler
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      e.preventDefault();
      onSendInput({ type: 'scroll', deltaX: e.deltaX, deltaY: e.deltaY });
    },
    [controlsEnabled, session?.session_type, onSendInput]
  );

  // Keyboard handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      e.preventDefault();
      onSendInput({
        type: 'keyboard',
        keyType: 'down',
        key: e.key,
        code: e.code,
        modifiers: {
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        },
      });
    },
    [controlsEnabled, session?.session_type, onSendInput]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (!controlsEnabled || session?.session_type !== 'full_control') return;
      e.preventDefault();
      onSendInput({
        type: 'keyboard',
        keyType: 'up',
        key: e.key,
        code: e.code,
        modifiers: {
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        },
      });
    },
    [controlsEnabled, session?.session_type, onSendInput]
  );

  // Prevent context menu on right click
  const handleContextMenu = useCallback(
    (e: MouseEvent) => {
      if (controlsEnabled && session?.session_type === 'full_control') {
        e.preventDefault();
      }
    },
    [controlsEnabled, session?.session_type]
  );

  // Session not started
  if (!session) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-muted/30 rounded-lg border-2 border-dashed p-8',
          className
        )}
      >
        <MonitorOff className="h-16 w-16 text-muted-foreground mb-4" />
        <p className="text-muted-foreground text-center">
          No active remote session
        </p>
      </div>
    );
  }

  // Connection state: connecting
  if (connectionState === 'connecting') {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-muted/30 rounded-lg border p-8',
          className
        )}
      >
        <div className="relative">
          <Monitor className="h-16 w-16 text-primary" />
          <div className="absolute -bottom-1 -right-1">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
        </div>
        <p className="mt-4 text-lg font-medium">Connecting to remote session...</p>
        <p className="text-sm text-muted-foreground mt-1">
          Waiting for the cashier to share their screen
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={onEndSession}
          disabled={isEndingSession}
        >
          {isEndingSession ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <PhoneOff className="h-4 w-4 mr-2" />
          )}
          Cancel
        </Button>
      </div>
    );
  }

  // Connection failed
  if (connectionState === 'failed' || error) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-red-500/5 rounded-lg border border-red-500/20 p-8',
          className
        )}
      >
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <p className="text-lg font-medium text-red-600">Connection Failed</p>
        <p className="text-sm text-muted-foreground mt-1 text-center max-w-md">
          {error || 'Unable to establish a connection to the remote session'}
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={onEndSession}
          disabled={isEndingSession}
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div
        ref={containerRef}
        className={cn(
          'flex flex-col bg-black rounded-lg overflow-hidden',
          isFullscreen ? 'fixed inset-0 z-50' : 'border',
          className
        )}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <StatusBadge state={connectionState} />
            <ConnectionQuality quality={session.connection_quality} />
            {session.session_type === 'full_control' && (
              <Badge variant="outline" className="gap-1.5 border-blue-500/30 text-blue-400">
                <MousePointer className="h-3 w-3" />
                Full Control
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Control toggle (only for full control mode) */}
            {session.session_type === 'full_control' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'h-8 gap-1.5',
                      controlsEnabled ? 'text-blue-400' : 'text-zinc-500'
                    )}
                    onClick={() => setControlsEnabled(!controlsEnabled)}
                  >
                    {controlsEnabled ? (
                      <>
                        <Mouse className="h-4 w-4" />
                        <Keyboard className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        <Mouse className="h-4 w-4 opacity-50" />
                        <Keyboard className="h-4 w-4 opacity-50" />
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {controlsEnabled ? 'Disable remote control' : 'Enable remote control'}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Fullscreen toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-white"
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              </TooltipContent>
            </Tooltip>

            {/* End session */}
            <Button
              variant="destructive"
              size="sm"
              className="h-8 gap-1.5"
              onClick={onEndSession}
              disabled={isEndingSession}
            >
              {isEndingSession ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PhoneOff className="h-4 w-4" />
              )}
              End Session
            </Button>
          </div>
        </div>

        {/* Video container */}
        <div
          className="flex-1 relative bg-zinc-950 flex items-center justify-center overflow-hidden"
          tabIndex={0}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onContextMenu={handleContextMenu}
          style={{
            cursor: controlsEnabled && session.session_type === 'full_control' ? 'none' : 'default',
          }}
        >
          {stream ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-zinc-500">
              <Monitor className="h-16 w-16 mb-4" />
              <p>Waiting for video stream...</p>
            </div>
          )}

          {/* Control mode indicator overlay */}
          {controlsEnabled && session.session_type === 'full_control' && stream && (
            <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-blue-400 flex items-center gap-2">
              <Mouse className="h-3 w-3" />
              Remote control active - Click to interact
            </div>
          )}
        </div>

        {/* Status bar */}
        <div className="flex items-center justify-between px-4 py-1.5 bg-zinc-900 border-t border-zinc-800 text-xs text-zinc-500">
          <span>
            Session ID: {session.id.slice(0, 8)}...
          </span>
          {videoDimensions.width > 0 && (
            <span>
              {videoDimensions.width} x {videoDimensions.height}
            </span>
          )}
          <span>
            {session.session_type === 'view_only' ? 'View Only' : 'Full Control'}
          </span>
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * Fullscreen modal wrapper for the remote session viewer.
 */
export function RemoteSessionViewerModal({
  open,
  onClose,
  ...props
}: Omit<RemoteSessionViewerProps, 'className'> & {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <RemoteSessionViewer
        {...props}
        className="w-full h-full max-w-[95vw] max-h-[90vh]"
        onEndSession={() => {
          props.onEndSession();
          onClose();
        }}
      />
    </div>
  );
}

